/**
 * Админ-панель онбординга: settings + список туров / toggle / CRUD / удаление через AlertDialog.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useModuleFlags } from "@/contexts/ModuleFlagsContext";
import {
  onboardingApi,
  type OnboardingTourOut,
} from "@/services/onboarding-api.service";
import { adminApi } from "@/services/api-v2.service";

export default function OnboardingAdminPanel() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { flags, refresh } = useModuleFlags();

  const settingsQuery = useQuery({
    queryKey: ["admin", "onboarding", "settings"],
    queryFn: () => onboardingApi.adminGetSettings(),
  });

  const toursQuery = useQuery({
    queryKey: ["admin", "onboarding", "tours"],
    queryFn: () => onboardingApi.adminListTours(),
  });

  const sysQuery = useQuery({
    queryKey: ["admin", "system-settings"],
    queryFn: () => adminApi.getSystemSettings(),
  });

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<OnboardingTourOut | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [formKey, setFormKey] = useState("");
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formRules, setFormRules] = useState("{}");
  const [formStepsJson, setFormStepsJson] = useState("[]");

  const tours = toursQuery.data?.items ?? [];

  const openCreate = () => {
    setEditing(null);
    setFormKey("");
    setFormName("");
    setFormDescription("");
    setFormRules('{"max_days": 14}');
    setFormStepsJson(
      JSON.stringify(
        [
          {
            step_order: 0,
            element_selector: '[data-onboarding="app-header"]',
            placement: "bottom",
            title: "Шапка",
            content: "Навигация и поиск",
            is_skippable: true,
          },
        ],
        null,
        2,
      ),
    );
    setEditorOpen(true);
  };

  const openEdit = (tour: OnboardingTourOut) => {
    setEditing(tour);
    setFormKey(tour.key);
    setFormName(tour.name);
    setFormDescription(tour.description || "");
    setFormRules(JSON.stringify(tour.trigger_rules || {}, null, 2));
    setFormStepsJson(
      JSON.stringify(
        (tour.steps || []).map((s) => ({
          step_order: s.order,
          element_selector: s.element_selector,
          placement: s.placement || "bottom",
          title: s.title,
          content: s.content,
          wait_for_selector: s.wait_for_selector,
          is_skippable: s.is_skippable ?? true,
          media_url: s.media_url,
        })),
        null,
        2,
      ),
    );
    setEditorOpen(true);
  };

  const saveTourMutation = useMutation({
    mutationFn: async () => {
      let rules: Record<string, unknown> = {};
      let steps: unknown[] = [];
      try {
        rules = JSON.parse(formRules || "{}") as Record<string, unknown>;
      } catch {
        throw new Error("trigger_rules: невалидный JSON");
      }
      try {
        steps = JSON.parse(formStepsJson || "[]") as unknown[];
      } catch {
        throw new Error("steps: невалидный JSON");
      }
      if (editing) {
        return onboardingApi.adminUpdateTour(editing.id, {
          name: formName.trim(),
          description: formDescription.trim() || null,
          trigger_rules: rules,
          steps: steps as never,
        });
      }
      return onboardingApi.adminCreateTour({
        key: formKey.trim(),
        name: formName.trim(),
        description: formDescription.trim() || null,
        trigger_rules: rules,
        steps: steps as never,
        is_active: true,
        is_default: false,
      });
    },
    onSuccess: () => {
      toast({ title: editing ? "Тур обновлён" : "Тур создан" });
      setEditorOpen(false);
      void qc.invalidateQueries({ queryKey: ["admin", "onboarding", "tours"] });
    },
    onError: (e: Error) => {
      toast({ title: "Ошибка", description: e.message, variant: "destructive" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (id: number) => onboardingApi.adminToggleTour(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "onboarding", "tours"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => onboardingApi.adminDeleteTour(id),
    onSuccess: () => {
      toast({ title: "Тур удалён" });
      setDeleteId(null);
      void qc.invalidateQueries({ queryKey: ["admin", "onboarding", "tours"] });
    },
  });

  const settingsMutation = useMutation({
    mutationFn: (patch: { global_switch?: boolean; show_for_existing?: boolean }) =>
      onboardingApi.adminPutSettings(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "onboarding", "settings"] });
      toast({ title: "Настройки онбординга сохранены" });
    },
  });

  const moduleFlagMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      adminApi.updateSystemSettings({ module_onboarding_enabled: enabled }),
    onSuccess: async () => {
      void qc.invalidateQueries({ queryKey: ["admin", "system-settings"] });
      await refresh();
      toast({ title: "Feature flag модуля обновлён" });
    },
  });

  const moduleEnabled = useMemo(() => {
    if (sysQuery.data?.module_onboarding_enabled != null) {
      return Boolean(sysQuery.data.module_onboarding_enabled);
    }
    return flags.moduleOnboardingEnabled;
  }, [sysQuery.data, flags.moduleOnboardingEnabled]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-medium">Онбординг (guided tours)</h3>
        <SectionHelpTooltip label="Справка по онбордингу">
          <p>
            <strong>Зачем:</strong> пошаговые туры по UI. Прогресс хранится на сервере.
          </p>
          <p>
            <strong>Как:</strong> feature flag — жёсткое отключение; global switch — мягкий OFF;
            toggle тура — точечное включение в пул.
          </p>
          <p>
            <strong>Опасно:</strong> удаление тура каскадно чистит шаги и прогресс — через диалог
            подтверждения.
          </p>
        </SectionHelpTooltip>
      </div>

      <div className="grid gap-4 rounded-lg border p-4 md:grid-cols-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label htmlFor="onb-module-flag">Модуль включён (feature flag)</Label>
            <p className="text-xs text-muted-foreground">module_onboarding_enabled</p>
          </div>
          <Switch
            id="onb-module-flag"
            checked={moduleEnabled}
            disabled={moduleFlagMutation.isPending}
            onCheckedChange={(v) => moduleFlagMutation.mutate(v)}
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label htmlFor="onb-global">Global switch</Label>
            <p className="text-xs text-muted-foreground">Мягкий выключатель туров</p>
          </div>
          <Switch
            id="onb-global"
            checked={settingsQuery.data?.global_switch ?? true}
            disabled={settingsMutation.isPending || settingsQuery.isLoading}
            onCheckedChange={(v) => settingsMutation.mutate({ global_switch: v })}
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label htmlFor="onb-existing">Show for existing</Label>
            <p className="text-xs text-muted-foreground">Туры для «старых» пользователей</p>
          </div>
          <Switch
            id="onb-existing"
            checked={settingsQuery.data?.show_for_existing ?? false}
            disabled={settingsMutation.isPending || settingsQuery.isLoading}
            onCheckedChange={(v) => settingsMutation.mutate({ show_for_existing: v })}
          />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h4 className="font-medium">Туры</h4>
        <Button type="button" size="sm" onClick={openCreate}>
          <Plus className="mr-1 h-4 w-4" />
          Создать
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Key</TableHead>
            <TableHead>Название</TableHead>
            <TableHead>Шагов</TableHead>
            <TableHead>Default</TableHead>
            <TableHead>Активен</TableHead>
            <TableHead className="w-[120px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {tours.map((t) => (
            <TableRow key={t.id}>
              <TableCell className="font-mono text-xs">{t.key}</TableCell>
              <TableCell>{t.name}</TableCell>
              <TableCell>{t.steps_count}</TableCell>
              <TableCell>{t.is_default ? "да" : "—"}</TableCell>
              <TableCell>
                <Switch
                  checked={t.is_active}
                  disabled={toggleMutation.isPending}
                  onCheckedChange={() => toggleMutation.mutate(t.id)}
                />
              </TableCell>
              <TableCell className="space-x-1 text-right">
                <Button type="button" variant="ghost" size="icon" onClick={() => openEdit(t)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setDeleteId(t.id)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {tours.length === 0 && !toursQuery.isLoading && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-muted-foreground">
                Туров пока нет
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto z-[230]">
          <DialogHeader>
            <DialogTitle>{editing ? `Редактировать: ${editing.key}` : "Новый тур"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {!editing && (
              <div>
                <Label htmlFor="tour-key">Key</Label>
                <Input id="tour-key" value={formKey} onChange={(e) => setFormKey(e.target.value)} />
              </div>
            )}
            <div>
              <Label htmlFor="tour-name">Название</Label>
              <Input id="tour-name" value={formName} onChange={(e) => setFormName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="tour-desc">Описание</Label>
              <Textarea
                id="tour-desc"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="tour-rules">trigger_rules (JSON)</Label>
              <Textarea
                id="tour-rules"
                className="font-mono text-xs"
                value={formRules}
                onChange={(e) => setFormRules(e.target.value)}
                rows={4}
              />
            </div>
            <div>
              <Label htmlFor="tour-steps">steps (JSON)</Label>
              <Textarea
                id="tour-steps"
                className="font-mono text-xs"
                value={formStepsJson}
                onChange={(e) => setFormStepsJson(e.target.value)}
                rows={12}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditorOpen(false)}>
              Отмена
            </Button>
            <Button
              type="button"
              disabled={saveTourMutation.isPending || !formName.trim() || (!editing && !formKey.trim())}
              onClick={() => saveTourMutation.mutate()}
            >
              {saveTourMutation.isPending ? "Сохранение…" : "Сохранить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId != null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="z-[230]">
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить тур?</AlertDialogTitle>
            <AlertDialogDescription>
              Будут удалены шаги и прогресс пользователей по этому туру. Действие нельзя откатить в UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleteId != null) deleteMutation.mutate(deleteId);
              }}
            >
              {deleteMutation.isPending ? "Удаление…" : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
